import React, { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { put } from "../redux/ApiHelper";
import { CometChatCalls } from "@cometchat/calls-sdk-javascript";
import { whenCometChatCallsReady } from "../services/cometChat.service";

type CallsUser = {
  uid?: string;
  getUid?: () => string;
};

const getUid = (user: CallsUser | null | undefined): string =>
  String(user?.uid ?? user?.getUid?.() ?? "");

const loginToCallsSdk = async (uid: string): Promise<CallsUser> => {
  const result = await CometChatCalls.login(uid);
  if (result && typeof result === "object" && "error" in result && result.error) {
    throw result.error;
  }
  if (result && typeof result === "object" && "user" in result) {
    return (result as { user: CallsUser }).user;
  }
  return result as CallsUser;
};

const CometChatUserHanlder = () => {
  const { signedAdmin } = useSelector((state: RootState) => state.admin);

  const createCometChatUser = useCallback(async () => {
    if (!signedAdmin?.email || !signedAdmin?.role) return;
    await put(`/admin/cometchat/user/create`, {
      adminEmail: signedAdmin.email,
      adminRole: signedAdmin.role,
    });
  }, [signedAdmin]);

  useEffect(() => {
    if (!signedAdmin?.id) return;

    let cancelled = false;

    const ensureLogin = async () => {
      try {
        await whenCometChatCallsReady();
        if (cancelled) return;

        const existing = (await CometChatCalls.getLoggedInUser()) as CallsUser | null;
        if (existing && getUid(existing) === String(signedAdmin.id)) {
          console.log("Calls SDK already logged in:", getUid(existing));
          return;
        }

        if (existing) {
          try {
            await CometChatCalls.logout();
          } catch {
            // ignore before fresh login
          }
        }

        try {
          const user = await loginToCallsSdk(String(signedAdmin.id));
          console.log("Calls SDK login successful:", { uid: getUid(user) });
        } catch (loginError) {
          console.log("Calls SDK login failed, creating user:", loginError);
          await createCometChatUser();
          if (cancelled) return;
          const user = await loginToCallsSdk(String(signedAdmin.id));
          console.log("Calls SDK login successful after create:", {
            uid: getUid(user),
          });
        }
      } catch (error) {
        console.log("Calls SDK ensure login failed:", error);
      }
    };

    void ensureLogin();

    return () => {
      cancelled = true;
    };
  }, [signedAdmin, createCometChatUser]);

  return null;
};

export default CometChatUserHanlder;
